/**
 * Android/iOS WebView 네이티브 브릿지 (S0 = 세션 전달만).
 * NFC·기기ID 등 나머지 브릿지는 S2 에서 추가.
 *
 * Android: window.BIZNOMI_BRIDGE.postMessage()
 * iOS:     window.webkit.messageHandlers.BIZNOMI_BRIDGE.postMessage()
 */

declare global {
  interface Window {
    BIZNOMI_BRIDGE?: { postMessage: (msg: string) => void }
    IS_NATIVE_APP?: boolean
    __NATIVE_PLATFORM__?: string
    webkit?: { messageHandlers?: { BIZNOMI_BRIDGE?: { postMessage: (msg: string) => void } } }
  }
}

/**
 * 앱 안인가.
 *
 * 🔴 신호를 **셋 다** 본다 (2026-09-07). 전에는 브리지 객체 둘만 봤는데,
 *   그러면 `SAVE_SESSION` 이 조용히 안 나간다 — `if (!isNativeApp()) return` 이라
 *   에러도 안 난다.
 *
 *   세션이 네이티브에 안 남으면 앱이 다음에 켤 때 **localStorage 를 통째로 지운다**
 *   (`ios-native/Views/WebView/WebViewContainer.swift` 의 `restoredSession == nil` 분기).
 *   그래서 회사 코드도 토큰도 매번 사라지고, 켤 때마다 회사 코드를 다시 물었다.
 *
 * ⚠ 이 앱 안에 판정 함수가 **둘**이었다. `lib/device.ts` 는 `__NATIVE_PLATFORM__` 과
 *   `IS_NATIVE_APP` 을 보고, 여기는 브리지 객체만 봤다. 그래서 **NFC 태깅은 되는데
 *   세션 저장만 안 되는** 모양이 나왔다. 신호가 갈리면 증상도 갈린다.
 *   `apps/client` 는 셋을 다 보는 함수 하나뿐이다 (`lib/nfc.ts`).
 */
export function isNativeApp(): boolean {
  if (typeof window === 'undefined') return false
  if (window.IS_NATIVE_APP) return true
  if (window.__NATIVE_PLATFORM__) return true
  return Boolean(window.BIZNOMI_BRIDGE || window.webkit?.messageHandlers?.BIZNOMI_BRIDGE)
}

function post(message: string) {
  const ios = window.webkit?.messageHandlers?.BIZNOMI_BRIDGE
  if (ios) {
    ios.postMessage(message)
    return
  }
  window.BIZNOMI_BRIDGE?.postMessage(message)
}

/** 네이티브 Keychain 에 세션 저장 — 앱 재시작 시 로그인 유지 */
export function saveSessionToNative(session: {
  accessToken: string
  refreshToken: string
  user: { userId: string; email?: string | null; name: string; role: string; organizationId: string; plan: string }
}) {
  if (!isNativeApp()) return
  post(
    JSON.stringify({
      action: 'SAVE_SESSION',
      requestId: `session_${Date.now()}`,
      payload: {
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
        user: { ...session.user, email: session.user.email || '' },
      },
    }),
  )
}

export function signOutFromNative() {
  if (!isNativeApp()) return
  post(JSON.stringify({ action: 'SIGN_OUT', requestId: `signout_${Date.now()}` }))
}
